import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TripsDashboard from "./TripsDashboard";

const ProfilePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [localUser, setLocalUser] = useState(user);
  const [previewImage, setPreviewImage] = useState(user?.avatar || "");
  const [curatorStatus, setCuratorStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    location: "",
  });

  const ENABLE_CURATOR = true;

  useEffect(() => {
    const fetchCuratorStatus = async () => {
      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token");

        if (!token) return;

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/curator/my-application`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();
        setCuratorStatus(data);
      } catch (err) {
        console.error("Curator fetch error", err);
      }
    };

    fetchCuratorStatus();
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    setEditForm({
      name: user.name || "",
      phone: user.phone || "",
      location: user.location || "",
    });
  }, [user, navigate]);

  /* ===== IMAGE UPLOAD (LOCAL PREVIEW) ===== */
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/upload/avatar`,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await res.json();

    setPreviewImage(data.imageUrl); // ✅ now URL
  };

  /* ===== SAVE PROFILE ===== */
  const handleSave = async (e) => {
    e.preventDefault();

    setLoading(true); // 🔥 start loading

    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: editForm.name,
            phone: editForm.phone,
            location: editForm.location,
            avatar: previewImage,
          }),
        }
      );

      const updatedUser = await res.json();

      sessionStorage.setItem("user", JSON.stringify(updatedUser));
      setLocalUser(updatedUser);
      setIsEditing(false);

      // ✅ show success popup
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false); // 🔥 stop loading
    }
  };

  if (!localUser) return null;

  return (
    <div className="min-h-screen bg-[#f7f9f8] pt-24 px-4 pb-20 font-sans">
      <div className="max-w-[1000px] mx-auto space-y-8">
        
        {/* Page Header (Optional Back button) */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1.5 focus:outline-none">
            ← <span className="font-medium">Back</span>
          </button>
        </div>

        {/* SAAS DASHBOARD HEADER CARD */}
        <div className="bg-white rounded-3xl shadow-[0_4px_24px_rgb(0,0,0,0.02)] border border-gray-100 p-8 flex flex-col md:flex-row items-center md:items-start gap-8 relative overflow-hidden transition-all duration-300">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#5b6f00]/5 to-transparent z-0 pointer-events-none"></div>

          {/* Avatar Container */}
          <div className="relative z-10 flex-shrink-0">
            <div className="w-[120px] h-[120px] rounded-full overflow-hidden bg-gray-50 border-4 border-white shadow-[0_4px_12px_rgb(0,0,0,0.06)] ring-1 ring-[#5b6f00]/10 flex items-center justify-center relative group">
              {previewImage ? (
                <img src={previewImage} alt="Profile" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="w-full h-full text-4xl font-serif text-[#1f2d1f]/40 flex items-center justify-center bg-gray-100">
                  {localUser.name?.[0]?.toUpperCase()}
                </div>
              )}
              {isEditing && (
                <label className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs font-semibold cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  Change
                  <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                </label>
              )}
            </div>
          </div>

          {/* Profile Details */}
          <div className="flex-1 z-10 text-center md:text-left mt-2 md:mt-0 flex flex-col justify-center h-full">
            <h1 className="text-3xl font-serif font-bold text-[#1f2d1f]">{localUser.name}</h1>
            <p className="text-[15px] text-gray-500 mt-1">{localUser.email}</p>
            <div className="flex items-center justify-center md:justify-start gap-3 mt-4 text-[13px] text-gray-400 font-medium tracking-wide border-t border-gray-100 pt-4">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500"></span> Active account</span>
              <span>•</span>
              <span>Member since {new Date().getFullYear()}</span>
            </div>
          </div>

          {/* Primary Action */}
          <div className="z-10 mt-4 md:mt-6">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="px-6 py-2.5 rounded-full bg-white border border-gray-200 shadow-sm text-sm font-semibold text-[#1f2d1f] hover:bg-gray-50 hover:shadow-md hover:border-gray-300 transition-all duration-200 active:scale-95 flex items-center gap-2 focus:outline-none"
            >
              {isEditing ? (
                "View Profile"
              ) : (
                <>
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  Edit Profile
                </>
              )}
            </button>
          </div>
        </div>

        {/* DASHBOARD GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          
          {/* PROFILE INFO CARD */}
          <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgb(0,0,0,0.02)] border border-gray-100 p-6 sm:p-8 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 flex flex-col h-full">
            <h3 className="text-[15px] font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              Personal Details
            </h3>
            
            {!isEditing ? (
              <div className="space-y-6 flex-1 flex flex-col justify-center">
                <InfoItem icon="phone" label="Contact Number" value={localUser.phone || "Not provided"} />
                <InfoItem icon="location" label="Location" value={localUser.location || "Not provided"} />
              </div>
            ) : (
              <form onSubmit={handleSave} className="space-y-4 flex-1">
                <ModernInput label="Full Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                <ModernInput label="Phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                <ModernInput label="Location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
                
                <div className="flex gap-3 pt-4 border-t border-gray-100 mt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-5 py-2.5 rounded-xl bg-[#5b6f00] text-white font-semibold text-sm shadow-sm hover:bg-[#4a5a00] hover:shadow-md transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-70 disabled:active:scale-100 focus:outline-none"
                  >
                    {loading ? (
                      <span className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin"></span>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-all duration-200 active:scale-95 focus:outline-none"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="flex flex-col gap-6 md:gap-8">
            
            {/* ACCOUNT STATS CARD */}
            <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgb(0,0,0,0.02)] border border-gray-100 p-6 sm:p-8 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300">
              <h3 className="text-[15px] font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                Account Balance
              </h3>
              <div className="bg-[#f7f9f8] rounded-2xl p-5 border border-gray-200 flex items-center justify-between">
                <div>
                  <p className="text-[13px] font-medium text-gray-500 mb-1">Available Tokens</p>
                  <p className="text-3xl font-bold text-[#1f2d1f]">{localUser.tokens ?? 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#5b6f00]/10 text-[#5b6f00] flex items-center justify-center text-xl">
                  🪙
                </div>
              </div>
            </div>

            {/* CURATOR SECTION HIGHLIGHT CARD */}
            {ENABLE_CURATOR && (
              <div className="bg-gradient-to-br from-[#1f2d1f] to-[#2a3c2a] rounded-[24px] shadow-[0_8px_30px_rgb(31,45,31,0.15)] p-6 sm:p-8 text-white relative overflow-hidden group">
                {/* Background design */}
                <div className="absolute top-0 right-0 -m-8 w-32 h-32 bg-white/5 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-serif font-semibold text-white">Creator Program</h3>
                    <span className="bg-white/10 border border-white/20 text-white/90 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm backdrop-blur-md">
                      Premium
                    </span>
                  </div>
                  <p className="text-[14px] text-white/70 mb-6">Earn tokens by curating and sharing your travel itineraries with the community.</p>

                  {!curatorStatus?.applied && (
                    <button
                      onClick={() => navigate("/curator/apply")}
                      className="w-full px-5 py-3 rounded-xl bg-[#5b6f00] text-white font-semibold text-sm shadow-[0_4px_14px_rgba(91,111,0,0.4)] hover:bg-[#a8cc00] hover:text-[#1f2d1f] transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 focus:outline-none"
                    >
                      🚀 Become a Curator
                    </button>
                  )}

                  {curatorStatus?.applied && curatorStatus.status === "pending" && (
                    <div className="w-full px-5 py-3 rounded-xl bg-orange-500/20 border border-orange-500/30 text-orange-200 text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-md">
                      ⏳ Application Pending Review
                    </div>
                  )}

                  {curatorStatus?.applied && curatorStatus?.status === "approved" && (
                    <button
                      onClick={() => navigate("/curator/dashboard")}
                      className="w-full px-5 py-3 rounded-xl bg-white text-[#1f2d1f] font-semibold text-sm shadow-[0_4px_14px_rgba(255,255,255,0.2)] hover:bg-gray-100 transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 focus:outline-none"
                    >
                      🎯 Creator Dashboard <span className="text-lg leading-none">→</span>
                    </button>
                  )}

                  {curatorStatus?.status === "rejected" && (
                    <div className="w-full px-5 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-200 text-sm font-medium flex flex-col items-center justify-center gap-1 backdrop-blur-md text-center">
                      <span>❌ Application Not Approved</span>
                      <button onClick={() => navigate("/curator/apply")} className="text-white underline text-xs mt-1 hover:text-gray-200 focus:outline-none">Submit again</button>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ACTIONS PILLS GRID */}
        <div className="bg-white rounded-3xl shadow-[0_4px_24px_rgb(0,0,0,0.02)] border border-gray-100 p-6 flex flex-wrap gap-4 items-center justify-center sm:justify-start">
          <button
            onClick={() => navigate("/tokens")}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-[#1f2d1f] font-medium text-sm hover:border-[#5b6f00] hover:bg-[#5b6f00]/5 transition-all duration-200 active:scale-95 flex items-center gap-2 shadow-sm focus:outline-none"
          >
            <span className="text-lg">🪙</span> Token History
          </button>

          <button
            onClick={onLogout}
            className="px-5 py-2.5 rounded-xl border border-red-100 bg-red-50 text-red-600 font-medium text-sm hover:bg-red-100 transition-all duration-200 active:scale-95 flex items-center gap-2 shadow-sm ml-auto sm:ml-0 focus:outline-none"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            Sign Out
          </button>
        </div>

        {/* TRIPS DASHBOARD */}
        <div className="mt-12">
          <TripsDashboard />
        </div>

      </div>

      {/* TOAST UPGRADE */}
      <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 bg-[#1f2d1f]/90 backdrop-blur-xl text-white px-8 py-3.5 rounded-full shadow-[0_8px_30px_rgb(31,45,31,0.2)] border border-white/10 text-[15px] font-medium z-50 flex items-center gap-3 transition-all duration-500 transform ${showToast ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0 pointer-events-none'}`}>
        <div className="w-6 h-6 rounded-full bg-[#5b6f00] flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
        </div>
        Profile updated successfully
      </div>

    </div>
  );
};

export default ProfilePage;

/* ===== SMALL UI HELPERS ===== */

const InfoItem = ({ icon, label, value }) => {
  const getIcon = () => {
    if (icon === "phone") return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>;
    if (icon === "location") return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
    return null;
  };

  return (
    <div className="flex items-center gap-4 p-3 rounded-2xl hover:bg-gray-50 transition-colors duration-200">
      <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
        {getIcon()}
      </div>
      <div>
        <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-[15px] font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
};

const ModernInput = ({ label, ...props }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[13px] font-semibold text-gray-600 pl-1">{label}</label>
    <input
      {...props}
      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-[15px] text-gray-900 bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all duration-200 shadow-sm"
    />
  </div>
);
